import { AppShell, Avatar, Badge, Burger, Group, Menu, NavLink, ScrollArea, Text, UnstyledButton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconLogout, IconUserCircle } from "@tabler/icons-react";
import { Link, Outlet, useLocation } from "react-router-dom";

import { navItems } from "@/features/registry";

import { hasAnyRole, surfaceOf } from "./roles";
import { useSession } from "./session";

export function AppLayout() {
  const { me, logout } = useSession();
  const [opened, { toggle, close }] = useDisclosure();
  const { pathname } = useLocation();
  const surface = surfaceOf(me);
  const items = navItems.filter((n) => n.surface === surface && hasAnyRole(me, n.roles));
  // Longest matching nav path is the active one (so /admin does not stay lit on /admin/staff).
  const active = items
    .filter((n) => pathname === n.path || pathname.startsWith(`${n.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0]?.path;

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 232, breakpoint: "md", collapsed: { mobile: !opened } }}
      padding="lg"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger opened={opened} onClick={toggle} hiddenFrom="md" size="sm" aria-label="Toggle navigation" />
            <img src="/logo.png" alt="Fireslots" height={30} />
            <Text fw={700} c="violet.4" component={Link} to="/">
              Back Office
            </Text>
            <Badge variant="light" color={surface === "portal" ? "teal" : "violet"}>
              {surface === "portal" ? "Partner portal" : "Staff"}
            </Badge>
          </Group>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <UnstyledButton data-testid="user-menu">
                <Group gap={8}>
                  <Avatar size="sm" radius="xl" color="violet">
                    {(me?.display_name || me?.email || "?").slice(0, 1).toUpperCase()}
                  </Avatar>
                  <Text size="sm" visibleFrom="sm">
                    {me?.display_name || me?.email}
                  </Text>
                  <IconChevronDown size={14} />
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{me?.email}</Menu.Label>
              <Menu.Item
                component={Link}
                to="/account"
                leftSection={<IconUserCircle size={16} />}
                data-testid="menu-account"
              >
                Account
              </Menu.Item>
              <Menu.Item
                color="red"
                leftSection={<IconLogout size={16} />}
                onClick={() => void logout()}
                data-testid="menu-logout"
              >
                Sign out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p="xs">
        <ScrollArea>
          {items.map((n) => (
            <NavLink
              key={n.path}
              component={Link}
              to={n.path}
              label={n.label}
              leftSection={<n.icon size={18} stroke={1.6} />}
              active={n.path === active}
              onClick={close}
              data-testid={`nav-${n.path}`}
            />
          ))}
        </ScrollArea>
      </AppShell.Navbar>
      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
